import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, X, CheckCheck, Loader2 } from 'lucide-react';
import { useApp } from '../context/useApp';
import { useAuth } from '../context/useAuth';
import { buildInvoiceApprovalEntry, invoiceApprovalPersonKey, approvedInvoiceKeys } from '../utils/invoiceApprovals';
import {
  PRIORITIES, DATE_RANGES, filterNotifications, notificationDealOptions,
  notificationTarget, notificationPriority,
} from '../utils/notificationFilters';

/**
 * NotificationCenter — bell icon + dropdown panel that lives in the
 * top header. Drives off the `notifications` state in AppContext;
 * realtime INSERT events from Supabase prepend new rows so the
 * panel stays live without a poll. Mark-read actions optimistically
 * stamp read_at on the local state and flush to the DB.
 *
 * Each notification can carry a link_path (e.g. /owners/<id>/dashboard)
 * — clicking the row marks it read, dismisses the panel, and
 * navigates to that path. Without a link, the row is informational
 * only and clicking just marks it read.
 */
/** One look for every control in the filter bar, so the row reads as a row. */
/** The unread bar's colour says WHY the row matters, not just that it is
 *  unread — red for somebody waiting on you, amber for something slipping,
 *  slate for the record. Carried on the bar rather than as another chip
 *  because the row is already dense. */
const PRIORITY_COLOR = {
  'needs-you': '#dc2626',
  'at-risk': '#d97706',
  fyi: '#64748b',
};

const selectStyle = {
  background: '#ffffff',
  border: '1px solid #cbd5e1',
  borderRadius: 0,
  color: '#334155',
  padding: '2px 5px',
  fontSize: '0.68rem',
  fontFamily: 'inherit',
  maxWidth: 130,
};

export default function NotificationCenter() {
  const navigate = useNavigate();
  const {
    notifications,
    markNotificationRead,
    markAllNotificationsRead,
    dismissNotification,
    getDealFull,
    updateDeal,
    addDealActivity,
    deals,
  } = useApp();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [actionId, setActionId] = useState(null);
  const wrapRef = useRef(null);
  // Two thousand rows across sixteen kinds, and the ones somebody is
  // waiting on sit between hundreds of stage changes. Not persisted: a
  // filter that survives a reload is a filter somebody forgets is on,
  // and an empty bell with no visible cause reads as "nothing happened".
  const [priority, setPriority] = useState('all');
  const [range, setRange] = useState('all');
  const [dealFilter, setDealFilter] = useState('all');
  const [unreadOnly, setUnreadOnly] = useState(false);

  const unreadCount = useMemo(
    () => (notifications || []).filter((n) => !n.readAt).length,
    [notifications],
  );
  const dealOptions = useMemo(
    () => notificationDealOptions(notifications, deals),
    [notifications, deals],
  );
  const shown = useMemo(
    () => filterNotifications(notifications, {
      priority, range, dealId: dealFilter, unreadOnly,
    }),
    [notifications, priority, range, dealFilter, unreadOnly],
  );
  const filtersOn = priority !== 'all' || range !== 'all' || dealFilter !== 'all' || unreadOnly;

  // Close on click-outside / Escape so the panel feels like a popover.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleRowClick = async (n) => {
    if (!n.readAt) markNotificationRead(n.id);
    // A message or a mention is about something somebody SAID, so it opens
    // the deal's discussion rather than dropping you on the deal to go
    // looking for it. Resolved here rather than at the writer so the
    // hundreds of message rows already in the table start working too.
    const target = notificationTarget(n);
    if (target) {
      setOpen(false);
      navigate(target);
    }
  };

  const approveInvoiceNotification = async (n) => {
    const dealId = n?.meta?.dealId;
    if (!dealId || !user?.id) return;
    // Who may approve. Same rule as the deal page: an agent approves
    // their own payout amount and nobody else's. OwnerDashboard's copy
    // of this check says it "must stay here, and in the queue" — this
    // is the queue, and it was missing.
    //
    // It matters more here than it looks, because a notification is not
    // the system speaking. The row belongs to the person it was sent
    // to, who may update it, so meta is THEIR input: approverKey,
    // approverProfileId and approverName were all read straight off it
    // and written into the deal as somebody's approval. The Approve
    // button itself only appears when meta.action is 'approve_invoice'.
    // Anyone who could set those four fields on a row they own could
    // record another agent's payout approval in their name, and
    // completing the set is what sends the "all agents approved" email.
    //
    // So identity comes from the session. A row naming anybody else is
    // refused rather than quietly re-pointed, because a legitimate
    // notification always names its own recipient (api/notify/
    // funded-invoice.js writes approverProfileId: recipient.id) — a
    // mismatch is never something that should be silently repaired.
    const claimedProfileId = n?.meta?.approverProfileId;
    if (claimedProfileId && String(claimedProfileId) !== String(user.id)) {
      alert('Only the assigned agent can approve their own payout amount.');
      return;
    }
    const key = invoiceApprovalPersonKey(user) || n?.meta?.approverKey;
    setActionId(n.id);
    try {
      const latest = await getDealFull?.(dealId);
      if (!latest?.id) throw new Error('Could not load this deal.');
      const existing = Array.isArray(latest.closingDocs) ? latest.closingDocs : [];
      if (!approvedInvoiceKeys(existing).has(key)) {
        const entry = buildInvoiceApprovalEntry({
          dealId,
          user,
          // The signed-in user, not the row's account of who they are.
          targetUser: { id: user.id, name: user.name, email: user.email },
        });
        await updateDeal(
          latest.id,
          { closingDocs: [...existing, entry] },
          latest.updatedAt ? { expectedUpdatedAt: latest.updatedAt } : undefined,
        );
        await addDealActivity?.({
          id: 'act-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6),
          dealId: latest.id,
          type: 'closing',
          text: `${entry.approvedForName || entry.approverName || 'Internal recipient'} approved commission payout from notification`,
          actorId: user.id,
          actorName: user.name || user.email || 'Unknown',
          createdAt: new Date().toISOString(),
          archived: false,
        });
      }
      markNotificationRead(n.id);
    } catch (err) {
      console.error('[NotificationCenter] invoice approval failed:', err);
      alert(err?.message || 'Could not approve invoice payout.');
    } finally {
      setActionId(null);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={`${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`}
        style={{
          position: 'relative',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 34, height: 34,
          background: open ? 'rgba(91,191,239,0.18)' : 'transparent',
          border: 'none', color: '#cbd5e1',
          cursor: 'pointer',
        }}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute', top: 4, right: 4,
              minWidth: 16, height: 16, padding: '0 4px',
              background: '#ef4444', color: '#ffffff',
              fontSize: '0.6rem', fontWeight: 600,
              borderRadius: 8,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid #001d4a',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 1500,
            width: 440, maxWidth: '95vw', maxHeight: '72vh',
            background: '#ffffff', color: '#0f172a',
            border: '1px solid #003279',
            boxShadow: '0 16px 48px rgba(0,0,0,0.4)',
            display: 'flex', flexDirection: 'column',
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '0.6rem 0.85rem',
            background: '#003279', color: '#ffffff',
          }}>
            <Bell size={14} />
            <span style={{ fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Notifications {filtersOn ? `— ${shown.length} shown` : (unreadCount > 0 ? `(${unreadCount})` : '')}
            </span>
            <span style={{ flex: 1 }} />
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllNotificationsRead}
                title="Mark every notification as read"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  background: 'transparent', border: '1px solid rgba(255,255,255,0.4)',
                  color: '#ffffff',
                  padding: '2px 7px', fontSize: '0.65rem', fontWeight: 500,
                  cursor: 'pointer', borderRadius: 0,
                }}
              >
                <CheckCheck size={11} /> Mark all read
              </button>
            )}
          </div>

          {/* Filters. Not persisted on purpose — one that survives a
              reload is one somebody forgets is on, and an empty bell with
              no visible cause reads as "nothing happened". */}
          <div
            data-notification-filters
            style={{
              display: 'flex', flexWrap: 'wrap', gap: 5,
              padding: '0.45rem 0.6rem',
              borderBottom: '1px solid #e2e8f0', background: '#f8fafc',
            }}
          >
            <select
              data-notification-priority
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              title="Needs you: somebody is waiting on you. At risk: something is slipping. FYI: it happened."
              style={selectStyle}
            >
              <option value="all">All priorities</option>
              {PRIORITIES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <select
              data-notification-range
              value={range}
              onChange={(e) => setRange(e.target.value)}
              style={selectStyle}
            >
              {DATE_RANGES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
            <select
              data-notification-deal
              value={dealFilter}
              onChange={(e) => setDealFilter(e.target.value)}
              style={{ ...selectStyle, maxWidth: 150 }}
            >
              <option value="all">All deals</option>
              {dealOptions.map((d) => (
                <option key={d.id} value={d.id}>{d.name} ({d.count})</option>
              ))}
            </select>
            <button
              type="button"
              data-notification-unread-only
              onClick={() => setUnreadOnly((v) => !v)}
              style={{
                ...selectStyle,
                cursor: 'pointer',
                background: unreadOnly ? '#1e40af' : '#ffffff',
                color: unreadOnly ? '#ffffff' : '#334155',
                borderColor: unreadOnly ? '#1e40af' : '#cbd5e1',
                fontWeight: unreadOnly ? 700 : 500,
              }}
            >
              Unread
            </button>
            {filtersOn ? (
              <button
                type="button"
                data-notification-clear-filters
                onClick={() => { setPriority('all'); setRange('all'); setDealFilter('all'); setUnreadOnly(false); }}
                style={{ ...selectStyle, cursor: 'pointer', border: 'none', background: 'transparent', color: '#1d4ed8' }}
              >
                Clear
              </button>
            ) : null}
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {(!notifications || notifications.length === 0) && (
              <div style={{ padding: '2rem 1rem', textAlign: 'center', color: '#64748b', fontSize: '0.78rem' }}>
                Nothing yet. Stage changes, signed offers, and other events will show up here.
              </div>
            )}
            {/* Said separately from "nothing yet", because an empty list
                caused by a filter and an empty list caused by nothing
                happening are different facts. */}
            {notifications?.length > 0 && shown.length === 0 ? (
              <div data-notification-none-match style={{ padding: '1.6rem 1rem', textAlign: 'center', color: '#64748b', fontSize: '0.78rem' }}>
                No notifications match these filters.
                <button
                  type="button"
                  onClick={() => { setPriority('all'); setRange('all'); setDealFilter('all'); setUnreadOnly(false); }}
                  style={{ marginLeft: 6, background: 'transparent', border: 'none', color: '#1d4ed8', cursor: 'pointer', fontSize: '0.78rem', padding: 0 }}
                >
                  Clear them
                </button>
              </div>
            ) : null}
            {shown.map((n) => {
              const unread = !n.readAt;
              const isInvoiceApproval = n?.meta?.action === 'approve_invoice';
              const rowPriority = notificationPriority(n);
              return (
                <div
                  key={n.id}
                  onClick={() => handleRowClick(n)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '6px 1fr auto',
                    gap: 8, alignItems: 'flex-start',
                    padding: '0.55rem 0.7rem',
                    borderBottom: '1px solid #f1f5f9',
                    background: unread ? '#eff6ff' : '#ffffff',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = unread ? '#dbeafe' : '#f8fafc'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = unread ? '#eff6ff' : '#ffffff'; }}
                >
                  <div
                    data-notification-priority-bar={rowPriority}
                    title={unread ? PRIORITIES.find((p) => p.id === rowPriority)?.label : undefined}
                    style={{
                      width: 6, alignSelf: 'stretch',
                      background: unread ? (PRIORITY_COLOR[rowPriority] || '#1e40af') : 'transparent',
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: unread ? 700 : 500, color: '#0f172a' }}>
                      {n.title}
                    </div>
                    {n.message && (
                      <div style={{ fontSize: '0.72rem', color: '#475569', lineHeight: 1.35 }}>
                        {n.message}
                      </div>
                    )}
                    <div style={{ fontSize: '0.62rem', color: '#94a3b8', marginTop: 2 }}>
                      {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                    </div>
                    {isInvoiceApproval && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); approveInvoiceNotification(n); }}
                        disabled={actionId === n.id}
                        style={{
                          marginTop: 6,
                          alignSelf: 'flex-start',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '0.35rem 0.55rem',
                          background: '#065f46',
                          color: '#ffffff',
                          border: '1px solid #065f46',
                          borderRadius: 0,
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          cursor: actionId === n.id ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {actionId === n.id ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={12} />}
                        Approve Payout
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {unread && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); markNotificationRead(n.id); }}
                        title="Mark as read"
                        style={iconBtnStyle('#1e40af')}
                      >
                        <Check size={11} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); dismissNotification(n.id); }}
                      title="Dismiss"
                      style={iconBtnStyle('#94a3b8')}
                    >
                      <X size={11} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function iconBtnStyle(color) {
  return {
    background: 'transparent', border: 'none', color,
    cursor: 'pointer', padding: 2,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  };
}
